/** Date helpers. Everything works on `YYYY-MM-DD` strings in UTC so output never depends on the host time zone. */

export const DAY_MS = 86_400_000;

export const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
export const MONTH_NAMES_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Parses `YYYY-MM-DD` as UTC midnight. */
export const toUtc = (date: string): number => {
    const [y, m, d] = date.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
};

export const toIsoDate = (time: number): string => new Date(time).toISOString().slice(0, 10);

/** True for a real calendar date in `YYYY-MM-DD` form (rejects 2024-02-30). */
export const isIsoDate = (value: unknown): value is string =>
    typeof value === 'string' && ISO_DATE.test(value) && toIsoDate(toUtc(value)) === value;

export const addDays = (date: string, days: number): string =>
    toIsoDate(toUtc(date) + days * DAY_MS);

export const daysBetween = (from: string, to: string): number =>
    Math.round((toUtc(to) - toUtc(from)) / DAY_MS);

/** Same day `months` earlier/later, clamped to the end of shorter months. */
export const addMonths = (date: string, months: number): string => {
    const [y, m, d] = date.split('-').map(Number);
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const lastDay = new Date(
        Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
    ).getUTCDate();
    target.setUTCDate(Math.min(d, lastDay));
    return toIsoDate(target.getTime());
};

/** `Mon, Sep 23, 2026` */
export const formatDate = (date: string): string => {
    const time = new Date(toUtc(date));
    return `${WEEKDAY_NAMES[time.getUTCDay()]}, ${MONTH_NAMES[time.getUTCMonth()]} ${time.getUTCDate()}, ${time.getUTCFullYear()}`;
};

/** `September 23, 2026` */
export const formatLongDate = (date: string): string => {
    const time = new Date(toUtc(date));
    return `${MONTH_NAMES_LONG[time.getUTCMonth()]} ${time.getUTCDate()}, ${time.getUTCFullYear()}`;
};

/** `Sep 23, 2026` */
export const formatShortDate = (date: string): string => {
    const time = new Date(toUtc(date));
    return `${MONTH_NAMES[time.getUTCMonth()]} ${time.getUTCDate()}, ${time.getUTCFullYear()}`;
};
